import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import './Contact.css';

const SUCCESS_REDIRECT_DELAY = 6000; // ms — matches the fade animation duration

const initialForm = { name: '', email: '', message: '', company: '' };
const MESSAGE_MAX_LEN = 500;

// Keep in sync with aws/contact-form/index.mjs — message must be plain
// text: no links, no markup.
const URL_RE =
    /(https?:\/\/|www\.)\S+|\b[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.(?:com|net|org|io|co|gov|edu|info|biz|dev|app|xyz|me|ly|tv|us|uk|ca|gg|shop|store|site|online)\b/i;
const HTML_RE = /[<>]/;

function Contact() {
    const [form, setForm] = useState(initialForm);
    const [status, setStatus] = useState('idle'); // idle | sending | success | error
    const [errorMessage, setErrorMessage] = useState('');
    // Sticks for the rest of this visit once someone tries to slip a link
    // in, even after they remove it and successfully resubmit — rides
    // along on the eventual submission so it shows up in the email/record.
    const [linkAttempted, setLinkAttempted] = useState(false);
    const navigate = useNavigate();

    const endpoint = process.env.REACT_APP_CONTACT_API_URL;

    // On success, let the floating confirmation fade in/out, then send
    // them back home — no lingering on an empty contact page.
    useEffect(() => {
        if (status !== 'success') return undefined;
        const timer = setTimeout(() => navigate('/'), SUCCESS_REDIRECT_DELAY);
        return () => clearTimeout(timer);
    }, [status, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setStatus('error');
            setErrorMessage('Name, email, and message are all required.');
            return;
        }
        if (form.message.length > MESSAGE_MAX_LEN) {
            setStatus('error');
            setErrorMessage(`Message is limited to ${MESSAGE_MAX_LEN} characters.`);
            return;
        }
        if (URL_RE.test(form.message)) {
            setStatus('error');
            setErrorMessage('Links aren’t allowed in the message — plain text only, please.');
            setLinkAttempted(true);
            // Best-effort: let the backend log the actual attempt (it runs
            // the same check and writes a flagged record) without making
            // the visitor wait on it — they already have their answer.
            if (endpoint) {
                fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form),
                }).catch(() => {});
            }
            return;
        }
        if (HTML_RE.test(form.message)) {
            setStatus('error');
            setErrorMessage('The message can’t contain HTML tags — plain text only, please.');
            return;
        }

        if (!endpoint) {
            // Not wired up yet (e.g. running locally before the backend is
            // deployed). Fail gracefully instead of throwing.
            setStatus('error');
            setErrorMessage('The contact form isn’t connected yet — please reach out through one of the links below instead.');
            return;
        }

        setStatus('sending');
        setErrorMessage('');

        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, linkAttempted }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Something went wrong sending your message.');
            }

            setStatus('success');
            setForm(initialForm);
            setLinkAttempted(false);
        } catch (err) {
            setStatus('error');
            setErrorMessage(err.message || 'Something went wrong sending your message.');
        }
    };

    return (
        <div className='generalContainer'>
            <h3 className='page-heading'>Contact</h3>
            <div className='contact-page'>
                {status === 'success' ? (
                    <div className='contact-success-float' role='status'>
                        <p>Thanks — your message is on its way.</p>
                        <p className='contact-success-float-sub'>Taking you back home…</p>
                    </div>
                ) : (
                    <form className='contact-form' onSubmit={handleSubmit} noValidate>
                        <label className='contact-label' htmlFor='contact-name'>
                            Name
                        </label>
                        <input
                            id='contact-name'
                            className='contact-input'
                            type='text'
                            name='name'
                            value={form.name}
                            onChange={handleChange}
                            autoComplete='name'
                            required
                        />

                        <label className='contact-label' htmlFor='contact-email'>
                            Email
                        </label>
                        <input
                            id='contact-email'
                            className='contact-input'
                            type='email'
                            name='email'
                            value={form.email}
                            onChange={handleChange}
                            autoComplete='email'
                            required
                        />

                        <label className='contact-label' htmlFor='contact-message'>
                            Message
                        </label>
                        <textarea
                            id='contact-message'
                            className='contact-input contact-textarea'
                            name='message'
                            value={form.message}
                            onChange={handleChange}
                            rows={6}
                            maxLength={MESSAGE_MAX_LEN}
                            required
                        />
                        <span className='contact-counter'>
                            {form.message.length} / {MESSAGE_MAX_LEN} · plain text only, no links
                        </span>

                        {/* Honeypot — hidden from real visitors, catches basic bots. */}
                        <div className='contact-honeypot' aria-hidden='true'>
                            <label htmlFor='contact-company'>Company</label>
                            <input
                                id='contact-company'
                                type='text'
                                name='company'
                                value={form.company}
                                onChange={handleChange}
                                tabIndex={-1}
                                autoComplete='off'
                            />
                        </div>

                        {status === 'error' && <p className='contact-error'>{errorMessage}</p>}

                        <button className='contact-submit' type='submit' disabled={status === 'sending'}>
                            {status === 'sending' ? 'Sending…' : 'Send'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

export default Contact;
